// Subject 7: the notifications journal of the signed-in person (GET /notifications),
// as the app shows it and keeps it for offline reading. Free of Capacitor and of
// the app's '@/' imports, so the unit tests run it under node.

/** What the phone keeps for offline reading, unless the admin set it: the 100 newest, none older than 60 days. */
export const JOURNAL_LIMIT = 100
export const JOURNAL_DAYS = 60

const DAY_MS = 24 * 60 * 60 * 1000

const count = (value, fallback) => (Number.isInteger(value) && value > 0 ? value : fallback)

/**
 * The copy as the admin set it (phone_copy of GET /notifications/config): on or
 * off, its rows and its days. Never synced, synced before the API sent it, or
 * unreadable: on, the 100 newest of the 60 last days.
 */
export function copyRules(config) {
    const copy = config?.phone_copy
    return {
        enabled: copy?.enabled !== false,
        items: count(copy?.items, JOURNAL_LIMIT),
        days: count(copy?.days, JOURNAL_DAYS),
    }
}

const time = (item) => Date.parse(item?.created_at ?? '') || 0

const newestFirst = (a, b) => time(b) - time(a)

/**
 * The journal once the first page is read again: that page, then what the copy
 * holds past its oldest row. Whatever the copy held within the span of that page
 * and the page no longer has (hidden elsewhere, purged) leaves. A page that is
 * the whole journal (complete: no next cursor) leaves nothing of the copy.
 */
export function mergeJournal(copy, page, { complete = false } = {}) {
    const fresh = (page ?? []).slice().sort(newestFirst)
    if (complete || fresh.length === 0) return complete ? fresh : (copy ?? []).slice().sort(newestFirst)

    const ids = new Set(fresh.map(({ id }) => id))
    const oldest = time(fresh.at(-1))
    const older = (copy ?? []).filter((item) => !ids.has(item.id) && time(item) < oldest)

    return [...fresh, ...older].sort(newestFirst)
}

/** The next page read by "load more", after what is shown, without doubles. */
export function appendJournal(items, more) {
    const ids = new Set((items ?? []).map(({ id }) => id))
    return [...(items ?? []), ...(more ?? []).filter(({ id }) => !ids.has(id))].sort(newestFirst)
}

/** What the phone keeps of the journal: its newest rows of the last days, as the rules ({ items, days }) say. */
export function offlineCopy(items, { items: limit, days } = copyRules(null), now = Date.now()) {
    return (items ?? []).filter((item) => now - time(item) <= days * DAY_MS).slice(0, limit)
}

/** The rows read: every one (ids null) or those given. */
export function markRead(items, ids = null, at = new Date().toISOString()) {
    const only = ids === null ? null : new Set(ids)
    return (items ?? []).map((item) => (item.read_at || (only && !only.has(item.id)) ? item : { ...item, read_at: at }))
}

export const unreadOf = (items) => (items ?? []).filter((item) => !item.read_at).length

/**
 * The filters above the journal. Every category falls under one of them, so no
 * row is ever out of reach of a filter.
 */
export const JOURNAL_FILTERS = [
    { key: 'all', categories: null },
    { key: 'sessions', categories: ['sessions', 'performance', 'sensor', 'recording'] },
    { key: 'health', categories: ['health'] },
    { key: 'programs', categories: ['programs'] },
    { key: 'sharing', categories: ['sharing'] },
    { key: 'account', categories: ['subscription', 'reminders', 'news', null] },
]

export function filterJournal(items, filterKey) {
    const filter = JOURNAL_FILTERS.find(({ key }) => key === filterKey)
    if (!filter?.categories) return items ?? []
    return (items ?? []).filter((item) => filter.categories.includes(item.category ?? null))
}

/**
 * The icon and the colour of a row, by theme: its category, with the types the
 * mockup draws apart (a sensor hit on a jump, a record or a goal against the
 * weekly summary).
 */
export function themeOf(item) {
    const type = item?.type ?? ''
    if (type === 'session.jump_sensor_hit') return { icon: 'bolt', tone: 'hit' }

    switch (item?.category) {
        case 'sessions':
        case 'recording':
            return { icon: 'activity', tone: 'sess' }
        case 'performance':
            return /record|goal/.test(type) ? { icon: 'trophy', tone: 'perf' } : { icon: 'chart', tone: 'perf' }
        case 'programs':
            return { icon: 'calendar', tone: 'prog' }
        case 'sharing':
            return { icon: 'users', tone: 'share' }
        case 'health':
            return { icon: 'heart', tone: 'health' }
        case 'sensor':
            return { icon: 'bt', tone: 'sensor' }
        case 'subscription':
            return { icon: 'card', tone: 'rem' }
        case 'news':
            return { icon: 'megaphone', tone: 'rem' }
        case 'reminders':
            return { icon: 'clock', tone: 'rem' }
        default:
            return { icon: 'bell', tone: 'rem' }
    }
}

const startOfDay = (at) => {
    const day = new Date(at)
    day.setHours(0, 0, 0, 0)
    return day.getTime()
}

/** today, yesterday, week (the 7 last days) or earlier, on the phone's own clock. */
export function dayOf(createdAt, now = new Date()) {
    const days = Math.round((startOfDay(now) - startOfDay(Date.parse(createdAt))) / DAY_MS)
    if (days <= 0) return 'today'
    if (days === 1) return 'yesterday'
    if (days < 7) return 'week'
    return 'earlier'
}

/** The rows by day group, in order, the empty groups left out. */
export function groupJournal(items, now = new Date()) {
    const groups = []
    for (const item of items ?? []) {
        const day = dayOf(item.created_at, now)
        if (groups.at(-1)?.day !== day) groups.push({ day, items: [] })
        groups.at(-1).items.push(item)
    }
    return groups
}

/** The time on the right of a row: 10:14 today and yesterday, mer. this week, 2 oct. before. */
export function timeOf(createdAt, locale, now = new Date()) {
    const at = new Date(Date.parse(createdAt))
    const day = dayOf(createdAt, now)
    if (day === 'today' || day === 'yesterday')
        return at.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    if (day === 'week') return at.toLocaleDateString(locale, { weekday: 'short' })
    return at.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        ...(at.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
    })
}
